import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactElement;
  allowedRoles?: Array<'brand' | 'creator' | 'admin' | 'agency'>;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles,
  redirectTo = '/authentication' 
}) => {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles && userProfile && !allowedRoles.includes(userProfile.role)) {
    console.log(`ProtectedRoute: User role '${userProfile.role}' not in allowed roles:`, allowedRoles);
    
    // Auto-redirect to their correct dashboard instead of showing error
    const dashboardRoutes: Record<string, string> = {
      brand: '/brand-dashboard',
      creator: '/creator-dashboard',
      admin: '/admin-dashboard',
      agency: '/agency-dashboard',
    };

    const correctRoute = dashboardRoutes[userProfile.role] || '/';
    console.log(`ProtectedRoute: Redirecting to correct dashboard: ${correctRoute}`);
    
    return <Navigate to={correctRoute} replace />;
  }

  return children;
};

export default ProtectedRoute;
